import { useEffect, useRef, useState, useCallback } from 'react';
import { APIProvider, Map, useMap } from '@vis.gl/react-google-maps';
import {
  buildingsInBbox, buildingsCountInBbox, createBuilding, updateBuilding, deleteBuilding, reportBuilding,
} from '../../lib/buildingsApi';
import { polygonGeometryToPath, pathToPolygonGeometry } from '../../utils/geoJson';
import { checkPolygon } from '../../utils/overlapCheck';
import { loadMapView, saveMapView } from '../../utils/mapViewStore';
import { useAuth } from '../../contexts/AuthContext';
import LocationSearch from './LocationSearch';
import KmlImportPanel from './KmlImportPanel';
import {
  StreetDrawTool, ObstacleDrawTool, PoleDropTool, EntryPointTool,
  InfrastructureLayer, InfraSelectionPanel,
} from './infrastructureTools';
import {
  deleteStreet as deleteStreetApi,
  deleteObstacle as deleteObstacleApi,
  deletePole as deletePoleApi,
  updateStreet as updateStreetApi,
  updateObstacle as updateObstacleApi,
  updatePole as updatePoleApi,
} from '../../lib/infrastructureApi';
import OperatorGuideModal from './OperatorGuideModal';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import { setBuildingUnits } from '../../lib/designApi';
import BuildingsRasterLayer, { MVT_MIN_ZOOM } from './BuildingsRasterLayer';
import { operatorCssColor } from '../../lib/operatorColor';

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Stable, well-spread colour per operator so every polygon visibly carries its
// author's identity at a glance (paired with the hover name pill below). A
// small string hash -> hue; saturation and lightness are fixed at values that
// stay legible over both satellite and roadmap tiles.
// Per-operator colour, shared with the deck.gl MVT layer via lib/operatorColor
// so a footprint keeps the same hue across the editable-Polygon and GPU layers.
function colorForOperator(id) {
  return operatorCssColor(id);
}

// Floating identity chip shown while the pointer is over a polygon. It is the
// answer to "who registered this?" without a click, and it also surfaces the
// (otherwise easy to miss) report affordance for an operator hovering another
// operator's polygon.
function HoverPill({ info }) {
  const { name, mine, canManage } = info;
  let text;
  if (mine) text = 'ساختمان شما';
  else if (canManage) text = `ثبت‌شده توسط ${name || 'اپراتور'} · کلیک برای مدیریت`;
  else text = `ثبت‌شده توسط ${name || 'اپراتور دیگر'} · برای گزارش مشکل کلیک کنید`;
  return (
    <div
      style={{
        position: 'absolute',
        top: 66,
        insetInlineStart: '50%',
        transform: 'translateX(-50%)',
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        maxWidth: 360,
        padding: '7px 14px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
        color: '#e2e8f0',
        fontFamily: "'Vazirmatn', sans-serif",
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1.4,
        direction: 'rtl',
        borderRadius: 10,
        border: '1px solid rgba(148,163,184,0.24)',
        boxShadow: '0 1px 0 rgba(255,255,255,0.06) inset, 0 6px 18px rgba(2,6,23,0.5)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          flexShrink: 0,
          borderRadius: '50%',
          background: mine ? '#2dd4bf' : (canManage ? '#fbbf24' : '#f87171'),
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{text}</span>
    </div>
  );
}

// Non-blocking instrument-panel alert that floats over the satellite imagery.
// Styling only: it must not gate map interaction or the building layer.
// tone 'error' (crimson, default) for failures; 'success' (teal) for
// confirmations such as a filed report.
// Building polygons load whenever the viewport holds a RENDERABLE number of them
// - so sparse annotation areas show at any working zoom (the pre-Qom behaviour),
// NOT gated behind a blunt zoom floor. Only two guards: skip province/country
// views where polygons are invisible specks (FAR_ZOOM), and skip a viewport so
// dense it can't be drawn (RENDER_CAP) - e.g. a whole dense city like Qom (200k+
// parcels) zoomed out. buildings_in_bbox is LIMITed server-side so it never times
// out; over the cap we clear the layer and show a "zoom in" hint.
const BUILDINGS_FAR_ZOOM = 11;
// Matches the buildings_in_bbox_json inner LIMIT (6001): a viewport with more
// than this renders nothing and shows the "zoom in" hint (google.maps.Polygon
// can't draw that many smoothly - the deck.gl/MVT layer is the next step for
// whole-city). Below it, every building in the viewport renders (no 1000 cap).
const BUILDINGS_RENDER_CAP = 6000;

// Phase 2: whole-city footprint overlay. When a viewport exceeds the Polygon
// render cap (a dense city zoomed out, e.g. Qom) we hide the editable Polygon
// layer and show server-rendered PNG footprint tiles via google.maps.ImageMapType
// (BuildingsRasterLayer), so the operator still sees every building. Set false to
// fall back to the plain "zoom in" hint over the cap.
const MVT_BUILDINGS_ENABLED = true;

function MapAlert({ message, tone = 'error' }) {
  const ok = tone === 'success';
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 64,
        insetInlineStart: 14,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        maxWidth: 320,
        padding: '9px 14px 9px 12px',
        background: ok
          ? 'linear-gradient(135deg, rgba(6,78,59,0.96) 0%, rgba(15,118,110,0.94) 100%)'
          : 'linear-gradient(135deg, rgba(127,29,29,0.96) 0%, rgba(153,27,27,0.94) 100%)',
        color: ok ? '#ecfdf5' : '#fef2f2',
        fontFamily: "'Vazirmatn', sans-serif",
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.5,
        direction: 'rtl',
        borderRadius: 10,
        border: ok
          ? '1px solid rgba(167,243,208,0.28)'
          : '1px solid rgba(254,202,202,0.28)',
        borderInlineStart: ok ? '3px solid #2dd4bf' : '3px solid #f87171',
        boxShadow: ok
          ? '0 1px 0 rgba(255,255,255,0.08) inset, 0 6px 18px rgba(6,78,59,0.5), 0 1px 3px rgba(0,0,0,0.4)'
          : '0 1px 0 rgba(255,255,255,0.08) inset, 0 6px 18px rgba(127,29,29,0.5), 0 1px 3px rgba(0,0,0,0.4)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          flexShrink: 0,
          background: ok ? 'rgba(167,243,208,0.16)' : 'rgba(254,202,202,0.16)',
          border: ok
            ? '1px solid rgba(167,243,208,0.3)'
            : '1px solid rgba(254,202,202,0.3)',
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        {ok ? '✓' : '!'}
      </span>
      <span>{message}</span>
    </div>
  );
}

// Phase 2a map toolbox. Six creation tools plus a guide button live in one
// dark-glass cluster: the operator picks a tool, the map clicks (or modal)
// behave per that tool, and the tool either auto-closes after a save or has
// its own close affordance. Building draw and KML import are the original
// pair; street / entry-point / obstacle / pole are the Phase 2a additions
// that give the routing engine the network data it needs.
function ToolButton({
  tool, label, title, icon, accent, onActivate,
}) {
  const [hover, setHover] = useState(false);
  const styles = {
    teal: {
      bg: hover
        ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
        : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
      color: '#f0fdfa',
      border: 'rgba(45,212,191,0.4)',
    },
    slate: {
      bg: hover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
      color: hover ? '#f1f5f9' : '#cbd5e1',
      border: 'rgba(148,163,184,0.32)',
    },
    sky: {
      bg: hover
        ? 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)'
        : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      color: '#f0f9ff',
      border: 'rgba(56,189,248,0.4)',
    },
    rose: {
      bg: hover
        ? 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)'
        : 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
      color: '#fef2f2',
      border: 'rgba(244,63,94,0.4)',
    },
  };
  const s = styles[accent] || styles.slate;
  return (
    <button
      type="button"
      onClick={() => onActivate(tool)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={title || label}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '8px 12px',
        background: s.bg,
        color: s.color,
        fontFamily: "'Vazirmatn', sans-serif",
        fontSize: 12.5,
        fontWeight: accent === 'slate' ? 600 : 700,
        lineHeight: 1,
        border: `1px solid ${s.border}`,
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'background 140ms ease, color 140ms ease',
        whiteSpace: 'nowrap',
      }}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

function MapToolbox({ onActivate }) {
  return (
    <div
      style={{
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        gap: 5,
        padding: 5,
        background: 'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
        borderRadius: 12,
        border: '1px solid rgba(148,163,184,0.22)',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <ToolButton
        tool="building"
        label="رسم ساختمان"
        accent="teal"
        icon={(
          <span aria-hidden="true" style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 18, height: 18, background: 'rgba(240,253,250,0.18)',
            border: '1px solid rgba(240,253,250,0.32)', borderRadius: 4,
            fontSize: 11, fontWeight: 800,
          }}>+</span>
        )}
        onActivate={onActivate}
      />
      <ToolButton
        tool="kml"
        label="بارگذاری KML"
        accent="slate"
        title="بارگذاری گروهی از فایلِ Google Earth"
        icon={(
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 3v12" />
            <path d="m7 8 5-5 5 5" />
            <path d="M5 21h14" />
          </svg>
        )}
        onActivate={onActivate}
      />
      <ToolButton
        tool="entry"
        label="نقطه‌ی ورود"
        accent="sky"
        title="تعیینِ نقطه‌ی ورودِ کابل به ساختمان"
        icon={(
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="10" r="3" />
            <path d="M12 21s-7-7-7-12a7 7 0 0 1 14 0c0 5-7 12-7 12Z" />
          </svg>
        )}
        onActivate={onActivate}
      />
      <ToolButton
        tool="street"
        label="رسم خیابان"
        accent="slate"
        title="رسمِ شبکه‌ی خیابان / کوچه / پیاده‌رو"
        icon={(
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M3 21h18" />
            <path d="M7 21V3" />
            <path d="M17 21V3" />
          </svg>
        )}
        onActivate={onActivate}
      />
      <ToolButton
        tool="obstacle"
        label="منطقه‌ی عبورناپذیر"
        accent="rose"
        title="رسمِ منطقه‌ی عبورناپذیر (رودخانه، ریل، محدوده‌ی ممنوع)"
        icon={(
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M5 5l14 14" />
          </svg>
        )}
        onActivate={onActivate}
      />
      <ToolButton
        tool="pole"
        label="تیر"
        accent="sky"
        title="افزودنِ تیر / پایه‌ی فیبرِ هوایی"
        icon={(
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 22V4" />
            <path d="M4 8h16" />
            <path d="M6 4h12" />
          </svg>
        )}
        onActivate={onActivate}
      />
    </div>
  );
}

// Bottom-left help cluster: two stacked buttons (operator guide + keyboard
// shortcuts) sitting just above the rotate control. Kept separate from the
// MapToolbox so it never shifts when the user is in the middle of a tool;
// and the buttons here are pure modal openers, they do not engage the map
// cursor at all.
function BottomLeftHelpCluster({ onGuide, onKeyboard }) {
  const [hover, setHover] = useState({ g: false, k: false });
  const btn = (hovered) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '7px 11px',
    background: hovered ? 'rgba(71,85,105,0.7)' : 'transparent',
    color: hovered ? '#f1f5f9' : '#cbd5e1',
    fontFamily: "'Vazirmatn', sans-serif",
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.2,
    border: '1px solid transparent',
    borderRadius: 7,
    cursor: 'pointer',
    transition: 'background 140ms ease, color 140ms ease',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  });
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 124,
        insetInlineStart: 14,
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        padding: 4,
        background: 'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
        borderRadius: 11,
        border: '1px solid rgba(148,163,184,0.22)',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <button
        type="button"
        onClick={onGuide}
        onMouseEnter={() => setHover((h) => ({ ...h, g: true }))}
        onMouseLeave={() => setHover((h) => ({ ...h, g: false }))}
        title="راهنمای کاملِ ابزارها"
        style={btn(hover.g)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" />
          <path d="M9.1 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <path d="M12 17h.01" />
        </svg>
        <span>راهنما</span>
      </button>
      <button
        type="button"
        onClick={onKeyboard}
        onMouseEnter={() => setHover((h) => ({ ...h, k: true }))}
        onMouseLeave={() => setHover((h) => ({ ...h, k: false }))}
        title="میانبرهای صفحه کلید"
        style={btn(hover.k)}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <path d="M6 10h.01" />
          <path d="M10 10h.01" />
          <path d="M14 10h.01" />
          <path d="M18 10h.01" />
          <path d="M7 14h10" />
        </svg>
        <span>میانبرهای صفحه کلید</span>
      </button>
    </div>
  );
}

// Top control strip. The search instrument and the draw control used to be
// two independently centered / edge-anchored overlays, so on narrower
// viewports the centered search slid under the draw button. They now share
// one flight-deck strip laid out as a 1fr | search | 1fr grid: the equal
// side tracks keep the search pixel-centered on screen, and because grid
// tracks can never overlap, the draw control (its own track, with an
// intrinsic minimum) is never crushed under the search at any width - it
// simply yields, the search shrinking gracefully on very narrow viewports.
function TopControlBar({ onActivate, onPick }) {
  return (
    <div
      style={{
        position: 'absolute',
        top: 14,
        insetInlineStart: 14,
        insetInlineEnd: 14,
        zIndex: 6,
        display: 'grid',
        gridTemplateColumns: '1fr minmax(0, 460px) 1fr',
        alignItems: 'start',
        gap: 12,
        direction: 'rtl',
        pointerEvents: 'none',
      }}
    >
      {/* Column 1 (visual right) is intentionally left empty: it mirrors the
          draw control's track so the search stays screen-centered, and keeps
          the top-right corner clear for the transient MapAlert. */}

      {/* Center track: the search instrument. */}
      <div style={{ gridColumn: 2, pointerEvents: 'auto' }}>
        <LocationSearch onPick={onPick} />
      </div>

      {/* Visual-left track: the draw control, hugged to the outer edge. */}
      <div
        style={{
          gridColumn: 3,
          justifySelf: 'end',
          pointerEvents: 'auto',
        }}
      >
        <MapToolbox onActivate={onActivate} />
      </div>
    </div>
  );
}

// Live drawing instrument, surfaced only while the operator is placing
// vertices. Mirrors the dark-glass shell of the other panels. The primary
// "finish" action stays disabled until a closed ring (3+ points) exists.
function DrawHUD({
  count, overlap = false, onFinish, onUndo, onCancel,
}) {
  const [finHover, setFinHover] = useState(false);
  const [undoHover, setUndoHover] = useState(false);
  const [cnlHover, setCnlHover] = useState(false);
  const canFinish = count >= 3 && !overlap;
  return (
    <div
      style={{
        position: 'absolute',
        top: 14,
        insetInlineStart: '50%',
        transform: 'translateX(-50%)',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 280,
        padding: '12px 14px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
        borderRadius: 13,
        border: '1px solid rgba(148,163,184,0.24)',
        borderTop: '3px solid #0d9488',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          color: '#e2e8f0',
          lineHeight: 1.5,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: '50%',
            background: overlap ? '#f87171' : '#2dd4bf',
            boxShadow: overlap
              ? '0 0 0 3px rgba(248,113,113,0.28)'
              : '0 0 0 3px rgba(45,212,191,0.25)',
          }}
        />
        <span>روی نقشه کلیک کنید تا گوشه‌های ساختمان مشخص شود</span>
      </div>
      <div
        style={{
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 11.5,
          fontWeight: 500,
          color: '#94a3b8',
          lineHeight: 1.5,
        }}
      >
        {count === 0
          ? 'هنوز نقطه‌ای ثبت نشده (حداقل ۳ نقطه لازم است)'
          : `${count} نقطه ثبت شد` + (canFinish ? ' - برای پایان روی «اتمام» بزنید' : '')}
      </div>
      {overlap && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            padding: '7px 10px',
            background: 'rgba(127,29,29,0.4)',
            border: '1px solid rgba(248,113,113,0.4)',
            borderRadius: 8,
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 11.5,
            fontWeight: 600,
            color: '#fecaca',
            lineHeight: 1.5,
          }}
        >
          <span aria-hidden="true" style={{ fontWeight: 800, flexShrink: 0 }}>!</span>
          <span>این محدوده با ساختمان دیگری همپوشانی دارد؛ پیش از اتمام آن را اصلاح کنید</span>
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onFinish}
          disabled={!canFinish}
          onMouseEnter={() => setFinHover(true)}
          onMouseLeave={() => setFinHover(false)}
          style={{
            flex: 1,
            padding: '9px 14px',
            background: !canFinish
              ? 'rgba(51,65,85,0.5)'
              : finHover
                ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
                : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
            color: !canFinish ? '#64748b' : '#f0fdfa',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            border: !canFinish
              ? '1px solid rgba(148,163,184,0.2)'
              : '1px solid rgba(45,212,191,0.4)',
            borderRadius: 8,
            cursor: canFinish ? 'pointer' : 'not-allowed',
            transition: 'background 140ms ease',
            boxShadow:
              canFinish && finHover
                ? '0 4px 14px rgba(13,148,136,0.45)'
                : '0 1px 0 rgba(255,255,255,0.1) inset',
          }}
        >
          اتمام
        </button>
        <button
          type="button"
          onClick={onUndo}
          disabled={count === 0}
          onMouseEnter={() => setUndoHover(true)}
          onMouseLeave={() => setUndoHover(false)}
          style={{
            padding: '9px 14px',
            background:
              count === 0
                ? 'rgba(51,65,85,0.4)'
                : undoHover
                  ? 'rgba(71,85,105,0.7)'
                  : 'rgba(51,65,85,0.55)',
            color: count === 0 ? '#64748b' : '#e2e8f0',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            border: '1px solid rgba(148,163,184,0.32)',
            borderRadius: 8,
            cursor: count === 0 ? 'not-allowed' : 'pointer',
            transition: 'background 140ms ease',
          }}
        >
          حذف نقطه
        </button>
        <button
          type="button"
          onClick={onCancel}
          onMouseEnter={() => setCnlHover(true)}
          onMouseLeave={() => setCnlHover(false)}
          style={{
            padding: '9px 14px',
            background: cnlHover ? 'rgba(127,29,29,0.5)' : 'rgba(51,65,85,0.55)',
            color: cnlHover ? '#fecaca' : '#e2e8f0',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            border: cnlHover
              ? '1px solid rgba(248,113,113,0.4)'
              : '1px solid rgba(148,163,184,0.32)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease, color 140ms ease',
          }}
        >
          لغو
        </button>
      </div>
    </div>
  );
}

// Segmented map-type control. Identifying buildings from satellite alone is
// hard, so the operator can flip to the flat roadmap view at any time. Shares
// the dark-glass HUD language and is anchored bottom-start so it never
// collides with the centered pending panel or the bottom-end selection panel.
// The third 'esri' option overlays Esri World Imagery — a flatter top-down
// view that helps when Google's satellite shows buildings at oblique angles
// (a known Iran-coverage limitation). Google satellite stays underneath as a
// fallback if an Esri tile fails to load.
function MapTypeToggle({ value, onChange }) {
  const opts = [
    { id: 'satellite', label: 'ماهواره‌ای' },
    { id: 'roadmap', label: 'نقشه' },
    { id: 'esri', label: 'Esri' },
  ];
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 22,
        insetInlineStart: 14,
        zIndex: 6,
        display: 'flex',
        gap: 4,
        padding: 5,
        background: 'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
        borderRadius: 11,
        border: '1px solid rgba(148,163,184,0.22)',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      {opts.map((o) => {
        const active = value === o.id;
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onChange(o.id)}
            aria-pressed={active}
            style={{
              padding: '7px 15px',
              background: active
                ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)'
                : 'transparent',
              color: active ? '#f0fdfa' : '#cbd5e1',
              fontFamily: "'Vazirmatn', sans-serif",
              fontSize: 12.5,
              fontWeight: active ? 700 : 600,
              lineHeight: 1,
              border: active
                ? '1px solid rgba(45,212,191,0.4)'
                : '1px solid transparent',
              borderRadius: 7,
              cursor: 'pointer',
              transition: 'background 140ms ease, color 140ms ease',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// View-rotation instrument. Operators place polygon vertices far faster when
// they can turn the satellite so a building's walls run square to the screen
// (otherwise every wall is a diagonal). Shares the MapTypeToggle's dark-glass
// language and sits just above it so the two view controls read as one
// cluster. The needle always points to true north and counter-rotates with
// the map, so it doubles as a compass. Rotation requires a vector map; on a
// raster map id setHeading is a silent no-op.
const TILT_STEPS = [0, 45, 67.5];

function MapRotateControl({ map }) {
  const [heading, setHeading] = useState(0);
  const [tilt, setTilt] = useState(0);

  useEffect(() => {
    if (!map) return undefined;
    const sync = () => {
      setHeading(Math.round(map.getHeading?.() || 0));
      setTilt(Math.round(map.getTilt?.() || 0));
    };
    sync();
    const lh = map.addListener('heading_changed', sync);
    const lt = map.addListener('tilt_changed', sync);
    return () => {
      window.google.maps.event.removeListener(lh);
      window.google.maps.event.removeListener(lt);
    };
  }, [map]);

  const rotateBy = useCallback((delta) => {
    if (!map) return;
    const next = ((((map.getHeading?.() || 0) + delta) % 360) + 360) % 360;
    map.setHeading(next);
  }, [map]);

  // Google Earth style oblique: step the camera down through the two useful
  // pitches and back to flat. 45 deg is the classic Earth view; 67.5 is the
  // steepest Google Maps vector allows and shows facades most clearly. Flat
  // (0) is best for precise polygon drawing, so reset-to-north also clears it.
  const cycleTilt = useCallback(() => {
    if (!map) return;
    const cur = Math.round(map.getTilt?.() || 0);
    const idx = TILT_STEPS.findIndex((t) => Math.abs(t - cur) < 6);
    const next = TILT_STEPS[(idx + 1) % TILT_STEPS.length];
    map.setTilt(next);
  }, [map]);

  const resetNorth = useCallback(() => {
    if (!map) return;
    map.setHeading(0);
    map.setTilt(0);
  }, [map]);

  const sideBtn = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 34,
    height: 30,
    padding: 0,
    background: 'transparent',
    color: '#cbd5e1',
    border: '1px solid transparent',
    borderRadius: 7,
    cursor: 'pointer',
    transition: 'background 140ms ease, color 140ms ease',
  };
  const hoverIn = (e) => {
    e.currentTarget.style.background = 'rgba(51,65,85,0.7)';
    e.currentTarget.style.color = '#f1f5f9';
  };
  const hoverOut = (e) => {
    e.currentTarget.style.background = 'transparent';
    e.currentTarget.style.color = '#cbd5e1';
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 70,
        insetInlineStart: 14,
        zIndex: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: 5,
        background:
          'linear-gradient(135deg, rgba(15,23,42,0.94) 0%, rgba(30,41,59,0.92) 100%)',
        borderRadius: 11,
        border: '1px solid rgba(148,163,184,0.22)',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 8px 24px rgba(2,6,23,0.55), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <button
        type="button"
        aria-label="چرخش پادساعتگرد"
        title="چرخش پادساعتگرد"
        onClick={() => rotateBy(-30)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={sideBtn}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 12a9 9 0 1 0 2.6-6.3" />
          <path d="M3 4v4.5h4.5" />
        </svg>
      </button>

      <button
        type="button"
        aria-label={`جهت فعلی ${heading} درجه؛ بازنشانی به شمال`}
        title={`جهت: ${heading}°  ·  کلیک برای بازنشانی به شمال  ·  میانبر: Ctrl را نگه دارید و بکشید تا نقشه بچرخد`}
        onClick={resetNorth}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          width: 44,
          height: 30,
          padding: 0,
          color: '#5eead4',
          background: 'rgba(45,212,191,0.10)',
          border: '1px solid rgba(45,212,191,0.28)',
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'background 140ms ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(45,212,191,0.18)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(45,212,191,0.10)'; }}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          aria-hidden="true"
          style={{ transform: `rotate(${-heading}deg)`, transition: 'transform 220ms ease' }}
        >
          <path d="M12 2 L7.5 14 L12 11.5 L16.5 14 Z" fill="currentColor" />
          <path d="M12 11.5 L12 21" stroke="currentColor" strokeWidth="1.6" opacity="0.45" />
        </svg>
      </button>

      <button
        type="button"
        aria-label="چرخش ساعتگرد"
        title="چرخش ساعتگرد"
        onClick={() => rotateBy(30)}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        style={sideBtn}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12a9 9 0 1 1 -2.6-6.3" />
          <path d="M21 4v4.5h-4.5" />
        </svg>
      </button>

      <span
        aria-hidden="true"
        style={{
          width: 1,
          height: 20,
          margin: '0 2px',
          background: 'rgba(148,163,184,0.28)',
        }}
      />

      <button
        type="button"
        aria-label={`نمای زاویه‌دار؛ شیب فعلی ${tilt} درجه`}
        title={`نمای ۴۵/۶۷ درجه برای دیدن نمای ساختمان‌ها (مثل Google Earth) · شیب فعلی ${tilt}° · میانبر: Ctrl را نگه دارید و اسکرول کنید`}
        onClick={cycleTilt}
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          width: 46,
          height: 30,
          padding: 0,
          color: tilt > 0 ? '#5eead4' : '#cbd5e1',
          background: tilt > 0 ? 'rgba(45,212,191,0.10)' : 'transparent',
          border: `1px solid ${tilt > 0 ? 'rgba(45,212,191,0.28)' : 'transparent'}`,
          borderRadius: 7,
          cursor: 'pointer',
          transition: 'background 140ms ease, color 140ms ease',
        }}
        onMouseEnter={(e) => {
          if (tilt > 0) { e.currentTarget.style.background = 'rgba(45,212,191,0.18)'; }
          else { e.currentTarget.style.background = 'rgba(51,65,85,0.7)'; e.currentTarget.style.color = '#f1f5f9'; }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = tilt > 0 ? 'rgba(45,212,191,0.10)' : 'transparent';
          e.currentTarget.style.color = tilt > 0 ? '#5eead4' : '#cbd5e1';
        }}
      >
        <svg width="16" height="14" viewBox="0 0 24 20" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 13 L12 4 L21 13 L12 18 Z" />
          <path d="M12 4 L12 18" opacity="0.4" />
        </svg>
        <span style={{ fontFamily: "'Vazirmatn', sans-serif", fontSize: 8.5, fontWeight: 700, lineHeight: 1 }}>
          {tilt > 0 ? `${tilt}°` : '۳بعد'}
        </span>
      </button>
    </div>
  );
}

// Confirm/discard instrument, surfaced only while a freshly drawn polygon is
// pending. Mirrors the dark-glass toolbar so the two read as one HUD.
function PendingPanel({ onSave, onCancel }) {
  const [saveHover, setSaveHover] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 22,
        insetInlineStart: '50%',
        transform: 'translateX(50%)',
        zIndex: 6,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 240,
        padding: '12px 14px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
        borderRadius: 13,
        border: '1px solid rgba(148,163,184,0.24)',
        borderTop: '3px solid #2563eb',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          color: '#e2e8f0',
          lineHeight: 1.5,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            flexShrink: 0,
            borderRadius: '50%',
            background: '#38bdf8',
            boxShadow: '0 0 0 3px rgba(56,189,248,0.25)',
          }}
        />
        <span>محدوده ساختمان را تایید کنید</span>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onSave}
          onMouseEnter={() => setSaveHover(true)}
          onMouseLeave={() => setSaveHover(false)}
          style={{
            flex: 1,
            padding: '9px 14px',
            background: saveHover
              ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
              : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
            color: '#f0fdfa',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            border: '1px solid rgba(45,212,191,0.4)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease',
            boxShadow: saveHover
              ? '0 4px 14px rgba(13,148,136,0.45)'
              : '0 1px 0 rgba(255,255,255,0.12) inset',
          }}
        >
          ذخیره
        </button>
        <button
          type="button"
          onClick={onCancel}
          onMouseEnter={() => setCancelHover(true)}
          onMouseLeave={() => setCancelHover(false)}
          style={{
            flex: 1,
            padding: '9px 14px',
            background: cancelHover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
            color: '#e2e8f0',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            border: '1px solid rgba(148,163,184,0.32)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease',
          }}
        >
          لغو
        </button>
      </div>
    </div>
  );
}

// Selection instrument. In 'edit' mode (the operator's own polygon) vertices
// are draggable and changes can be saved. In 'manage' mode (an admin acting on
// another operator's polygon) editing is disabled - the buildings update RLS
// is owner-only - so only delete is offered, with the owner and any open
// report count surfaced. Shares the dark-glass HUD shell with an amber caution
// accent, anchored bottom-end so it never collides with the centered panels.
function SelectionPanel({ mode = 'edit', ownerName, reportCount = 0, onSave, onDelete, buildingId }) {
  const [saveHover, setSaveHover] = useState(false);
  const [delHover, setDelHover] = useState(false);
  const [units, setUnits] = useState('');
  const isEdit = mode === 'edit';
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 22,
        insetInlineEnd: 14,
        zIndex: 7,
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        minWidth: 244,
        padding: '13px 15px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
        borderRadius: 13,
        border: '1px solid rgba(148,163,184,0.24)',
        borderTop: '3px solid #d97706',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          color: '#fde68a',
          lineHeight: 1.5,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'relative',
            display: 'inline-flex',
            width: 9,
            height: 9,
            flexShrink: 0,
            borderRadius: '50%',
            background: '#f59e0b',
            boxShadow: '0 0 0 3px rgba(245,158,11,0.22)',
          }}
        >
          <span
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: 'rgba(245,158,11,0.55)',
              animation: 'fttxSelPulse 1.6s ease-out infinite',
            }}
          />
        </span>
        <span>
          {isEdit
            ? 'گوشه‌ها را برای ویرایش بکشید'
            : `ساختمان ${ownerName || 'اپراتور دیگر'}${
                reportCount > 0 ? ` · گزارش‌شده (${reportCount})` : ''
              }`}
        </span>
        <style>{
          '@keyframes fttxSelPulse{0%{transform:scale(1);opacity:0.7}70%{transform:scale(2.6);opacity:0}100%{transform:scale(2.6);opacity:0}}'
        }</style>
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
        <span style={{ fontSize: 11.5, color: '#cbd5e1' }}>تعداد واحد:</span>
        <input
          type="number" min="0" value={units}
          onChange={(e) => setUnits(e.target.value)}
          style={{ width: 64, padding: '4px 6px', borderRadius: 6 }}
        />
        <button
          type="button"
          onClick={async () => {
            const n = parseInt(units, 10);
            if (Number.isFinite(n)) {
              const { error } = await setBuildingUnits(buildingId, n);
              if (error) window.alert('ذخیره‌ی تعداد واحد ناموفق بود');
            }
          }}
          style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11.5, cursor: 'pointer' }}
        >ذخیره</button>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onSave}
          onMouseEnter={() => setSaveHover(true)}
          onMouseLeave={() => setSaveHover(false)}
          style={{
            flex: 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '9px 14px',
            background: saveHover
              ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
              : 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
            color: '#fffbeb',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            border: '1px solid rgba(251,191,36,0.42)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease, transform 140ms ease',
            transform: saveHover ? 'translateY(-1px)' : 'translateY(0)',
            boxShadow: saveHover
              ? '0 4px 14px rgba(180,83,9,0.5)'
              : '0 1px 0 rgba(255,255,255,0.14) inset',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              flexShrink: 0,
              background: 'rgba(255,251,235,0.16)',
              border: '1px solid rgba(255,251,235,0.3)',
              borderRadius: 5,
              fontSize: 11,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            ✓
          </span>
          <span>ذخیره تغییر</span>
        </button>
        <button
          type="button"
          onClick={onDelete}
          onMouseEnter={() => setDelHover(true)}
          onMouseLeave={() => setDelHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 7,
            padding: '9px 14px',
            background: delHover
              ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
              : 'rgba(127,29,29,0.42)',
            color: delHover ? '#fef2f2' : '#fecaca',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            border: delHover
              ? '1px solid rgba(248,113,113,0.55)'
              : '1px solid rgba(248,113,113,0.32)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease, color 140ms ease',
            boxShadow: delHover ? '0 4px 14px rgba(153,27,27,0.5)' : 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 18,
              height: 18,
              flexShrink: 0,
              background: delHover ? 'rgba(254,242,242,0.16)' : 'rgba(248,113,113,0.14)',
              border: delHover
                ? '1px solid rgba(254,242,242,0.3)'
                : '1px solid rgba(248,113,113,0.3)',
              borderRadius: 5,
              fontSize: 12,
              fontWeight: 800,
              lineHeight: 1,
            }}
          >
            ×
          </span>
          <span>حذف</span>
        </button>
      </div>
    </div>
  );
}

// Report instrument. Surfaced when an operator selects a polygon they cannot
// manage (someone else's). A short reason is required; submitting files a
// building_reports row the admins review. Crimson caution accent so it never
// reads as a constructive action.
function ReportPanel({ ownerName, onSubmit, onCancel }) {
  const [reason, setReason] = useState('');
  const [sendHover, setSendHover] = useState(false);
  const [cnlHover, setCnlHover] = useState(false);
  const trimmed = reason.trim();
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 22,
        insetInlineEnd: 14,
        zIndex: 7,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: 300,
        padding: '13px 15px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
        borderRadius: 13,
        border: '1px solid rgba(148,163,184,0.24)',
        borderTop: '3px solid #dc2626',
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
      }}
    >
      <div
        style={{
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          color: '#fecaca',
          lineHeight: 1.5,
        }}
      >
        گزارش مشکل برای ساختمان {ownerName || 'اپراتور دیگر'}
      </div>
      <textarea
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={3}
        maxLength={500}
        placeholder="مشکل این محدوده را کوتاه توضیح دهید"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          resize: 'vertical',
          padding: '8px 10px',
          background: 'rgba(2,6,23,0.45)',
          color: '#e2e8f0',
          fontFamily: "'Vazirmatn', sans-serif",
          fontSize: 12.5,
          lineHeight: 1.6,
          border: '1px solid rgba(148,163,184,0.32)',
          borderRadius: 8,
          outline: 'none',
          direction: 'rtl',
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => onSubmit(trimmed)}
          disabled={trimmed.length === 0}
          onMouseEnter={() => setSendHover(true)}
          onMouseLeave={() => setSendHover(false)}
          style={{
            flex: 1,
            padding: '9px 14px',
            background: trimmed.length === 0
              ? 'rgba(51,65,85,0.5)'
              : sendHover
                ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                : 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
            color: trimmed.length === 0 ? '#64748b' : '#fef2f2',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 700,
            lineHeight: 1,
            border: trimmed.length === 0
              ? '1px solid rgba(148,163,184,0.2)'
              : '1px solid rgba(248,113,113,0.45)',
            borderRadius: 8,
            cursor: trimmed.length === 0 ? 'not-allowed' : 'pointer',
            transition: 'background 140ms ease',
          }}
        >
          ثبت گزارش
        </button>
        <button
          type="button"
          onClick={onCancel}
          onMouseEnter={() => setCnlHover(true)}
          onMouseLeave={() => setCnlHover(false)}
          style={{
            padding: '9px 14px',
            background: cnlHover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
            color: '#e2e8f0',
            fontFamily: "'Vazirmatn', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1,
            border: '1px solid rgba(148,163,184,0.32)',
            borderRadius: 8,
            cursor: 'pointer',
            transition: 'background 140ms ease',
          }}
        >
          لغو
        </button>
      </div>
    </div>
  );
}

function Layer({ operatorFilter = null, initialBounds = null, focusBuildingId = null, onDensityChange = null }) {
  const map = useMap();
  const { session } = useAuth();
  const userId = session?.user?.id || null;
  const polysRef = useRef([]);
  const entriesRef = useRef([]); // tiny markers for buildings that have an entry_point set
  const fittedRef = useRef(false);
  const viewRestoredRef = useRef(false);
  const tempRef = useRef(null);
  const selRef = useRef(null);
  const reportRef = useRef(null);
  const vertsRef = useRef([]);
  const previewRef = useRef(null);
  const drawRenderRef = useRef(() => {});
  const drawingRef = useRef(false);
  // True while the Ctrl rotate/tilt shortcut owns the pointer. Polygon and
  // draw clicks consult this so a Ctrl-drag never doubles as a vertex or a
  // selection.
  const ctrlRef = useRef(false);
  // Tracks whether a Phase 2a tool currently owns the map clicks (street,
  // obstacle, pole, entry). When true, the existing polygon click + hover
  // handlers bail so map clicks reach the active tool unambiguously.
  const otherToolRef = useRef(false);
  const [selId, setSelId] = useState(null);
  const [selMode, setSelMode] = useState('edit');
  const [selMeta, setSelMeta] = useState({ ownerName: null, reportCount: 0 });
  const [reportId, setReportId] = useState(null);
  const [reportOwner, setReportOwner] = useState(null);
  const [pending, setPending] = useState(false);
  const [drawing, setDrawing] = useState(false);
  const [vertCount, setVertCount] = useState(0);
  const [drawOverlap, setDrawOverlap] = useState(false);
  const [importing, setImporting] = useState(false);
  const [streetActive, setStreetActive] = useState(false);
  const [obstacleActive, setObstacleActive] = useState(false);
  const [poleActive, setPoleActive] = useState(false);
  const [entryActive, setEntryActive] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const [infraReloadKey, setInfraReloadKey] = useState(0);
  const [snapPoints, setSnapPoints] = useState([]);
  const [selectedInfra, setSelectedInfra] = useState(null);
  // Edit-in-progress for a previously-saved infra item. Holds the Google
  // overlay reference plus the original geometry so cancel can put it back.
  // While non-null, the parent suppresses other map interactions so the
  // operator can't drift into a half-edited state by clicking elsewhere.
  const [editingInfra, setEditingInfra] = useState(null);
  const editingInfraRef = useRef(null);
  useEffect(() => { editingInfraRef.current = editingInfra; }, [editingInfra]);
  const [mapType, setMapType] = useState('satellite');
  const [msg, setMsg] = useState(null);
  const [msgTone, setMsgTone] = useState('error');
  const [zoomGated, setZoomGated] = useState(false);
  // True when the viewport is over the Polygon cap AND zoomed enough that the GPU
  // MVT layer is actually drawing footprints (so the hint reads "overview, zoom to
  // edit" rather than "zoom in to show buildings").
  const [overviewMode, setOverviewMode] = useState(false);
  const [error, setError] = useState(null);
  const [hoverInfo, setHoverInfo] = useState(null);

  useEffect(() => { drawingRef.current = drawing; }, [drawing]);
  useEffect(() => {
    const toolActive = streetActive || obstacleActive || poleActive || entryActive;
    otherToolRef.current = toolActive;
    // Building polygons are clickable:true, so they SWALLOW the map's 'click'
    // event. A creation tool that needs a click landing on a building - above
    // all the entry-point tool, whose step 1 literally IS "click a building" -
    // would otherwise receive nothing and silently do nothing. While a tool
    // owns the cursor, make the buildings non-clickable so map clicks reach
    // the tool; restore them when the tool closes. (The building-draw flow
    // does the same via its own effect; the two are mutually exclusive.)
    polysRef.current.forEach((p) => {
      try { p.setOptions({ clickable: !toolActive && !drawingRef.current }); }
      catch (e) { /* overlay may be mid-teardown; the next reload heals it */ }
    });
  }, [streetActive, obstacleActive, poleActive, entryActive]);

  const clearPolys = useCallback(() => {
    polysRef.current.forEach((p) => p.setMap(null));
    polysRef.current = [];
    entriesRef.current.forEach((m) => m.setMap(null));
    entriesRef.current = [];
  }, []);

  // Shared overlap pre-check: does `geom` cover positive area of any loaded
  // polygon other than `excludeId`? Pure and defensive (never throws) so it is
  // safe to call live on every drawn vertex. The DB trigger remains the final
  // authority; this only makes the conflict visible far earlier.
  const overlapsLoaded = useCallback((geom, excludeId) => {
    try {
      const others = polysRef.current
        .filter((p) => p.__buildingId !== excludeId)
        .map((p) => p.__geometry)
        .filter(Boolean);
      return checkPolygon(geom, others).overlapsExisting;
    } catch {
      return false;
    }
  }, []);

  const reload = useCallback(async () => {
    if (!map) return;
    const b = map.getBounds();
    if (!b) return;
    // Skip only province/country-level views (polygons are invisible specks and
    // the fetch is pointless there).
    if ((map.getZoom() || 0) < BUILDINGS_FAR_ZOOM) {
      clearPolys();
      setZoomGated(true);
      setOverviewMode(false);
      if (onDensityChange) onDensityChange(false);
      return;
    }
    const j = b.toJSON(); // { west, south, east, north }
    // Density gate via a CHEAP capped count (not a full fetch). Only a dense city
    // zoomed out exceeds the cap; there we hand off to the raster footprint
    // overlay (BuildingsRasterLayer) instead of leaving the map blank, and we
    // skip fetching up to 6001 full rows (which competes with the tile renders
    // for the statement timeout). The raster draws from MVT_MIN_ZOOM up, so below
    // that we keep the plain "zoom in" hint (footprints are sub-pixel).
    const { count, error: ce } = await buildingsCountInBbox(j);
    if (ce) { setError('خطا در بارگذاری ساختمان‌ها'); return; }
    if (count > BUILDINGS_RENDER_CAP) {
      setError(null);
      clearPolys();
      setZoomGated(true);
      setOverviewMode(MVT_BUILDINGS_ENABLED && (map.getZoom() || 0) >= MVT_MIN_ZOOM);
      if (onDensityChange) onDensityChange(true);
      return;
    }
    // Renderable: fetch the full rows and draw the editable Polygon layer.
    const { rows, error: e } = await buildingsInBbox(j);
    if (e) { setError('خطا در بارگذاری ساختمان‌ها'); return; }
    setError(null);
    setZoomGated(false);
    setOverviewMode(false);
    if (onDensityChange) onDensityChange(false);
    clearPolys();
    // Scope the rendered polygons. focusBuildingId (admin reviewing ONE
    // reported polygon) is the most specific and wins: it isolates that single
    // building so the admin can find and act on it without the neighbours
    // getting in the way. Otherwise operatorFilter (admin "view one operator")
    // narrows to one author. With neither, every polygon in the bbox shows.
    let visible = rows;
    if (focusBuildingId) visible = rows.filter((r) => r.id === focusBuildingId);
    else if (operatorFilter) visible = rows.filter((r) => r.operator_id === operatorFilter);
    visible.forEach((row) => {
      const mine = row.is_mine;
      const reportCount = row.report_count || 0;
      const reported = reportCount > 0;
      const color = colorForOperator(row.operator_id);
      const poly = new window.google.maps.Polygon({
        paths: polygonGeometryToPath(row.geometry),
        fillColor: color,
        // Own polygons read a touch stronger so an operator still finds them
        // fast within the per-operator palette.
        fillOpacity: mine ? 0.45 : 0.3,
        // Reported polygons keep the amber caution outline (it must win over
        // the operator hue) so admins and the reporter spot them at a glance.
        strokeColor: reported ? '#f59e0b' : color,
        strokeWeight: reported ? 3 : (mine ? 2.5 : 1.5),
        // Everyone approved may click any polygon: owners/admins to manage it,
        // others to file a report. Inert while drawing (a click meant as a
        // vertex must not be swallowed) AND while any creation tool is active
        // (the tool needs the map click - e.g. the entry-point tool's "click a
        // building" step - which a clickable polygon would otherwise eat).
        // zIndex 100 keeps buildings on top of obstacle polygons / streets so
        // an overlap-area click selects the building (the more specific target).
        clickable: !drawingRef.current && !otherToolRef.current,
        zIndex: 100,
        map,
      });
      poly.__buildingId = row.id;
      poly.__mine = mine;
      poly.__canManage = row.can_manage;
      poly.__reportCount = reportCount;
      poly.__ownerName = row.owner_name || null;
      poly.__geometry = row.geometry;
      polysRef.current.push(poly);
      poly.addListener('click', () => {
        if (drawingRef.current || ctrlRef.current || otherToolRef.current) return;
        if (editingInfraRef.current) return;
        if (selRef.current && selRef.current !== poly) selRef.current.setEditable(false);
        reportRef.current = null;
        setReportId(null);
        setSelectedInfra(null);
        if (poly.__canManage) {
          // Migration 0015 lets admins UPDATE any building, not just delete -
          // so this branch enables editing regardless of ownership. selMode
          // still distinguishes 'edit' (own) from 'manage' (admin on another
          // operator's polygon) so the panel header keeps the right context
          // (owner name + open reports).
          poly.setEditable(true);
          selRef.current = poly;
          setSelMode(poly.__mine ? 'edit' : 'manage');
          setSelMeta({ ownerName: poly.__ownerName, reportCount: poly.__reportCount });
          setSelId(poly.__buildingId);
        } else {
          // Operator on someone else's polygon: report it.
          if (selRef.current) { selRef.current.setEditable(false); selRef.current = null; }
          setSelId(null);
          reportRef.current = poly;
          setReportOwner(poly.__ownerName);
          setReportId(poly.__buildingId);
        }
      });
      poly.addListener('mouseover', () => {
        if (drawingRef.current || ctrlRef.current || otherToolRef.current) return;
        if (editingInfraRef.current) return;
        setHoverInfo({
          name: poly.__ownerName,
          mine: poly.__mine,
          canManage: poly.__canManage,
        });
      });
      poly.addListener('mouseout', () => setHoverInfo(null));

      // Building entry point (where the drop terminates), if the owner has
      // set one. Rendered as a small teal-rimmed white dot sitting exactly on
      // the building's edge. Non-clickable: the building polygon stays the
      // selectable target.
      if (row.entry_point && Array.isArray(row.entry_point.coordinates)) {
        const [elng, elat] = row.entry_point.coordinates;
        if (Number.isFinite(elng) && Number.isFinite(elat)) {
          const dot = new window.google.maps.Circle({
            center: { lat: elat, lng: elng },
            radius: 1.4,
            fillColor: '#f0fdfa',
            fillOpacity: 1,
            strokeColor: '#0d9488',
            strokeOpacity: 1,
            strokeWeight: 1.8,
            clickable: false,
            map,
          });
          entriesRef.current.push(dot);
        }
      }
    });
  }, [map, clearPolys, operatorFilter, focusBuildingId, onDensityChange]);

  useEffect(() => {
    if (!map) return;
    const l = map.addListener('idle', reload);
    return () => { window.google.maps.event.removeListener(l); clearPolys(); };
  }, [map, reload, clearPolys]);

  // Keep the rendered map type in sync with the toggle. The type is driven
  // imperatively (not via the <Map> prop) so the toggle stays authoritative
  // and vis.gl never reverts it on re-render.
  //
  // The 'esri' case is special: Google has no built-in Esri imagery, so we
  // overlay an ImageMapType pointing at the Esri World Imagery XYZ tile
  // server on top of a Google satellite base. The Google satellite stays
  // underneath as a safety net (it shows through if an Esri tile 404s or is
  // missing for the area). On transitions away from 'esri' we strip the
  // overlay; the _isEsri tag makes the lookup robust against any future
  // overlay layers other code might insert.
  useEffect(() => {
    if (!map) return undefined;
    const overlays = map.overlayMapTypes;
    // Defensive: strip any prior Esri overlay before applying new state, so
    // rapid toggling can never stack duplicates.
    for (let i = overlays.getLength() - 1; i >= 0; i -= 1) {
      if (overlays.getAt(i)?._isEsri) overlays.removeAt(i);
    }
    if (mapType === 'esri') {
      map.setMapTypeId('satellite');
      const esri = new window.google.maps.ImageMapType({
        getTileUrl: ({ x, y }, zoom) =>
          `https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${zoom}/${y}/${x}`,
        tileSize: new window.google.maps.Size(256, 256),
        maxZoom: 19,
        opacity: 1,
        name: 'Esri World Imagery',
      });
      esri._isEsri = true;
      overlays.insertAt(0, esri);
      return () => {
        for (let i = overlays.getLength() - 1; i >= 0; i -= 1) {
          if (overlays.getAt(i)?._isEsri) overlays.removeAt(i);
        }
      };
    }
    map.setMapTypeId(mapType);
    return undefined;
  }, [map, mapType]);

  // Ctrl shortcut for the vector camera. Holding Ctrl suspends the map's own
  // pan/zoom/draw (gestureHandling 'none') and hands the pointer to us so a
  // drag turns the view (heading) instead of moving it, and the wheel pitches
  // the camera (tilt) instead of zooming - the keyboard-free twin of the
  // bottom-left rotate buttons. Releasing Ctrl (or losing focus) restores
  // normal gestures. Rotation/tilt need a vector map; on a raster id setHeading
  // /setTilt are silent no-ops, which degrades safely to "Ctrl does nothing".
  useEffect(() => {
    if (!map) return undefined;
    const div = map.getDiv();
    let active = false; // Ctrl currently held
    let dragging = false; // a rotate-drag is in progress
    let startX = 0;
    let startHeading = 0;
    // Coalesce camera updates onto a single animation frame. Mousemove +
    // wheel fire faster than the vector renderer paints, so writing
    // setHeading on every event produces visible stutter. moveCamera is the
    // Maps JS API designed to update multiple camera fields in one batch.
    let pendingHeading = null;
    let pendingTilt = null;
    let rafId = 0;
    const flush = () => {
      rafId = 0;
      const cam = {};
      if (pendingHeading != null) { cam.heading = pendingHeading; pendingHeading = null; }
      if (pendingTilt != null) { cam.tilt = pendingTilt; pendingTilt = null; }
      if (cam.heading != null || cam.tilt != null) map.moveCamera(cam);
    };
    const schedule = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(flush);
    };

    const setCursor = (c) => map.setOptions({ draggableCursor: c });
    const restCursor = () => (drawingRef.current ? 'crosshair' : null);

    const enter = () => {
      if (active) return;
      active = true;
      ctrlRef.current = true;
      map.setOptions({ gestureHandling: 'none' });
      setCursor('ew-resize');
    };
    const exit = () => {
      if (!active) return;
      active = false;
      dragging = false;
      ctrlRef.current = false;
      map.setOptions({ gestureHandling: 'greedy' });
      setCursor(restCursor());
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
      pendingHeading = null;
      pendingTilt = null;
    };

    const onKeyDown = (e) => { if (e.key === 'Control' || e.ctrlKey) enter(); };
    const onKeyUp = (e) => { if (e.key === 'Control' || !e.ctrlKey) exit(); };

    const onMouseDown = (e) => {
      if (!active) return;
      dragging = true;
      startX = e.clientX;
      startHeading = map.getHeading?.() || 0;
      setCursor('grabbing');
      e.preventDefault();
      e.stopPropagation();
    };
    const onMouseMove = (e) => {
      if (!active || !dragging) return;
      // ~0.35 deg/px: a comfortable ~1000 px swipe = one full revolution
      // without overshoot on small drags. Coalesced onto the next frame.
      const dx = e.clientX - startX;
      pendingHeading = (((startHeading + dx * 0.35) % 360) + 360) % 360;
      schedule();
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      if (active) setCursor('ew-resize');
    };

    const onWheel = (e) => {
      if (!active) return;
      e.preventDefault();
      e.stopPropagation();
      // Proportional to deltaY so mouse wheel notches (~100/notch -> ~5 deg)
      // feel responsive while trackpad two-finger scroll (small deltas)
      // glides smoothly. Clamped to the Maps vector tilt range [0, 67.5].
      const cur = pendingTilt != null ? pendingTilt : (map.getTilt?.() || 0);
      pendingTilt = Math.max(0, Math.min(67.5, cur - e.deltaY * 0.05));
      schedule();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', exit);
    window.addEventListener('mousemove', onMouseMove, true);
    window.addEventListener('mouseup', onMouseUp, true);
    if (div) {
      div.addEventListener('mousedown', onMouseDown, true);
      div.addEventListener('wheel', onWheel, { capture: true, passive: false });
    }

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', exit);
      window.removeEventListener('mousemove', onMouseMove, true);
      window.removeEventListener('mouseup', onMouseUp, true);
      if (div) {
        div.removeEventListener('mousedown', onMouseDown, true);
        div.removeEventListener('wheel', onWheel, true);
      }
      if (rafId) cancelAnimationFrame(rafId);
      if (active) map.setOptions({ gestureHandling: 'greedy' });
    };
  }, [map]);

  // Admin "view one operator" entry: fit the camera once to that operator's
  // polygon extent so their work is on screen immediately (the default Tehran
  // view would otherwise show nothing). fitBounds fires `idle`, which the
  // reload listener already handles, so the filtered layer loads with no extra
  // wiring.
  useEffect(() => {
    if (!map || fittedRef.current || !initialBounds) return;
    const b = initialBounds;
    const nums = [b.min_lng, b.min_lat, b.max_lng, b.max_lat];
    if (nums.some((v) => !Number.isFinite(v))) return;
    fittedRef.current = true;
    const bounds = new window.google.maps.LatLngBounds(
      { lat: b.min_lat, lng: b.min_lng },
      { lat: b.max_lat, lng: b.max_lng },
    );
    map.fitBounds(bounds, 64);
  }, [map, initialBounds]);

  // Restore the operator's last viewport per user (localStorage). Skipped
  // when initialBounds is set, because that path is the admin's "view one
  // operator" entry and should always frame that operator's polygons, not
  // wherever the admin themself last was. Runs once per session.
  useEffect(() => {
    if (!map || initialBounds || viewRestoredRef.current || !userId) return;
    const v = loadMapView(userId);
    if (v && v.center) {
      map.setCenter(v.center);
      if (Number.isFinite(v.zoom)) map.setZoom(v.zoom);
    }
    viewRestoredRef.current = true;
  }, [map, userId, initialBounds]);

  // Save the current viewport on every map idle (after every pan / zoom /
  // search). Idle is a natural debounce - it fires only when the camera
  // settles. Suppressed in the admin filtered view so admins do not pollute
  // their own operator workspace default with whatever bbox they just framed.
  useEffect(() => {
    if (!map || !userId || initialBounds) return undefined;
    const onIdle = () => {
      try {
        const c = map.getCenter();
        if (!c) return;
        saveMapView(userId, {
          center: { lat: c.lat(), lng: c.lng() },
          zoom: map.getZoom(),
        });
      } catch (e) { /* localStorage might be unavailable; ignore */ }
    };
    const l = map.addListener('idle', onIdle);
    return () => window.google.maps.event.removeListener(l);
  }, [map, userId, initialBounds]);

  // Fly to a searched place. panTo + a close zoom triggers the map's `idle`
  // event, which the reload listener already handles - so the building layer
  // refreshes for the new viewport with no extra wiring.
  const goTo = useCallback(({ lat, lng }) => {
    if (!map || !Number.isFinite(lat) || !Number.isFinite(lng)) return;
    map.panTo({ lat, lng });
    map.setZoom(17);
  }, [map]);

  const finishDraw = useCallback(() => {
    const verts = vertsRef.current;
    if (verts.length < 3) {
      setMsg('برای ساخت ساختمان حداقل سه نقطه لازم است');
      return;
    }
    // Defense in depth behind the disabled button: never let an overlapping
    // ring become a pending polygon.
    let geomChk;
    try { geomChk = pathToPolygonGeometry(verts); } catch { geomChk = null; }
    if (geomChk && overlapsLoaded(geomChk, null)) {
      setMsgTone('error');
      setMsg('این محدوده با ساختمان دیگری همپوشانی دارد؛ پیش از اتمام آن را اصلاح کنید');
      return;
    }
    const poly = new window.google.maps.Polygon({
      paths: verts,
      fillColor: '#0d9488',
      fillOpacity: 0.35,
      strokeColor: '#0d9488',
      strokeWeight: 2,
      editable: true,
      map,
    });
    tempRef.current = poly;
    vertsRef.current = [];
    setVertCount(0);
    setDrawOverlap(false);
    setDrawing(false);
    setPending(true);
  }, [map, overlapsLoaded]);

  // Manual click-to-draw. The google.maps.drawing library was deprecated in
  // August 2025 and removed in the May 2026 Maps JS release, so its
  // DrawingManager no longer produces a polygon (setDrawingMode is a silent
  // no-op). We collect vertices from core map click events instead, which is
  // a stable API and Google's recommended replacement. Edit/delete is
  // unaffected because it uses core Polygon.setEditable, not this library.
  useEffect(() => {
    if (!map || !drawing) return;
    polysRef.current.forEach((p) => p.setOptions({ clickable: false }));
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });

    // The growing ring is shown by the preview polygon's fill and stroke.
    // No per-vertex markers: google.maps.Marker is deprecated, and the
    // outline alone reads clearly while keeping the console clean. Finishing
    // is the explicit "اتمام" button, always visible in the draw HUD.
    const render = () => {
      const verts = vertsRef.current;
      if (!previewRef.current) {
        previewRef.current = new window.google.maps.Polygon({
          clickable: false,
          fillColor: '#0d9488',
          fillOpacity: 0.3,
          strokeColor: '#0d9488',
          strokeWeight: 2,
          map,
        });
      }
      previewRef.current.setPath(verts);
      // Live overlap guard: the moment the ring covers another building paint
      // it crimson and flag it, so "اتمام" is blocked before the operator ever
      // reaches the save step.
      let bad = false;
      if (verts.length >= 3) {
        try { bad = overlapsLoaded(pathToPolygonGeometry(verts), null); } catch { bad = false; }
      }
      previewRef.current.setOptions({
        fillColor: bad ? '#dc2626' : '#0d9488',
        strokeColor: bad ? '#dc2626' : '#0d9488',
      });
      setDrawOverlap(bad);
      setVertCount(verts.length);
    };
    drawRenderRef.current = render;

    const onClick = (e) => {
      // Ctrl is the rotate/tilt modifier - a Ctrl-click turns the camera,
      // it must never drop a vertex.
      if (ctrlRef.current) return;
      if (!e.latLng) return;
      vertsRef.current.push({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      render();
    };
    const cl = map.addListener('click', onClick);
    render();

    return () => {
      window.google.maps.event.removeListener(cl);
      map.setOptions({ draggableCursor: prevCursor || null, disableDoubleClickZoom: false });
      polysRef.current.forEach((p) => p.setOptions({ clickable: true }));
      if (previewRef.current) { previewRef.current.setMap(null); previewRef.current = null; }
      drawRenderRef.current = () => {};
    };
  }, [map, drawing, overlapsLoaded]);

  const startDraw = useCallback(() => {
    setMsg(null);
    setHoverInfo(null);
    if (selRef.current) {
      selRef.current.setEditable(false);
      selRef.current = null;
      setSelId(null);
    }
    reportRef.current = null;
    setReportId(null);
    vertsRef.current = [];
    setVertCount(0);
    setDrawOverlap(false);
    setDrawing(true);
  }, []);

  const submitReport = useCallback(async (reason) => {
    const poly = reportRef.current;
    if (!poly || !reason) return;
    const { error: e } = await reportBuilding(poly.__buildingId, reason);
    if (e) { setMsgTone('error'); setMsg('ثبت گزارش ناموفق بود'); return; }
    reportRef.current = null;
    setReportId(null);
    setMsgTone('success');
    setMsg('گزارش شما ثبت شد. متشکریم.');
    reload();
  }, [reload]);

  const cancelReport = useCallback(() => {
    reportRef.current = null;
    setReportId(null);
    setMsg(null);
  }, []);

  const undoVertex = useCallback(() => {
    if (vertsRef.current.length === 0) return;
    vertsRef.current = vertsRef.current.slice(0, -1);
    drawRenderRef.current();
  }, []);

  const cancelDrawMode = useCallback(() => {
    vertsRef.current = [];
    setVertCount(0);
    setDrawOverlap(false);
    setDrawing(false);
    setMsg(null);
  }, []);

  const cancelDraw = useCallback(() => {
    tempRef.current?.setMap(null);
    tempRef.current = null;
    setPending(false);
    setMsg(null);
  }, []);

  const saveDraw = useCallback(async () => {
    const poly = tempRef.current;
    if (!poly) return;
    setMsgTone('error');
    const path = poly.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    let geom;
    try {
      geom = pathToPolygonGeometry(path);
    } catch {
      setMsg('شکل پولیگان نامعتبر است');
      return;
    }
    const loaded = polysRef.current.map((p) => p.__geometry).filter(Boolean);
    const { selfIntersects, overlapsExisting } = checkPolygon(geom, loaded);
    if (selfIntersects) { setMsg('شکل پولیگان نامعتبر است'); return; }
    if (overlapsExisting) { setMsg('این محدوده با ساختمان دیگری همپوشانی دارد'); return; }
    const { error: e } = await createBuilding(geom);
    if (e) {
      const m = e.message || '';
      setMsg(
        m.includes('BUILDING_OVERLAP')
          ? 'این محدوده با ساختمان دیگری همپوشانی دارد'
          : m.includes('INVALID_GEOMETRY')
            ? 'شکل پولیگان نامعتبر است'
            : 'ذخیره ناموفق بود'
      );
      return;
    }
    poly.setMap(null);
    tempRef.current = null;
    setPending(false);
    setMsg(null);
    reload();
  }, [reload]);

  // Persist edits to a selected polygon. The overlap pre-check must compare
  // against every OTHER loaded geometry but exclude the polygon being edited,
  // otherwise the shape would always read as overlapping itself.
  const saveEdit = useCallback(async () => {
    const poly = selRef.current;
    if (!poly) return;
    setMsgTone('error');
    const path = poly.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    let geom;
    try {
      geom = pathToPolygonGeometry(path);
    } catch {
      setMsg('شکل پولیگان نامعتبر است');
      return;
    }
    const others = polysRef.current
      .filter((p) => p.__buildingId !== poly.__buildingId)
      .map((p) => p.__geometry)
      .filter(Boolean);
    const { selfIntersects, overlapsExisting } = checkPolygon(geom, others);
    if (selfIntersects) { setMsg('شکل پولیگان نامعتبر است'); return; }
    if (overlapsExisting) { setMsg('این محدوده با ساختمان دیگری همپوشانی دارد'); return; }
    const { error: e } = await updateBuilding(poly.__buildingId, geom);
    if (e) {
      const m = e.message || '';
      setMsg(
        m.includes('BUILDING_OVERLAP')
          ? 'این محدوده با ساختمان دیگری همپوشانی دارد'
          : m.includes('INVALID_GEOMETRY')
            ? 'شکل پولیگان نامعتبر است'
            : 'ذخیره ناموفق بود'
      );
      return;
    }
    selRef.current = null;
    setSelId(null);
    setMsg(null);
    reload();
  }, [reload]);

  // Delete works for an owner deleting their own polygon and for an admin
  // deleting anyone's: the buildings delete RLS allows owner OR admin.
  const removeSel = useCallback(async () => {
    const poly = selRef.current;
    if (!poly) return;
    const { error: e } = await deleteBuilding(poly.__buildingId);
    if (e) { setMsgTone('error'); setMsg('حذف ناموفق بود'); return; }
    selRef.current = null;
    setSelId(null);
    setSelMode('edit');
    setMsg(null);
    reload();
  }, [reload]);

  // One-tool-at-a-time orchestration. When the user picks a tool from the
  // toolbox, cancel anything currently in flight (building draw, pending
  // confirm, selection, report, other tools, modals), then activate the
  // requested tool. Building keeps using the legacy startDraw flow; the
  // Phase 2a tools use their own active-flag components.
  const activateTool = useCallback((next) => {
    if (drawingRef.current) {
      vertsRef.current = [];
      setVertCount(0);
      setDrawOverlap(false);
      setDrawing(false);
    }
    if (tempRef.current) {
      tempRef.current.setMap(null);
      tempRef.current = null;
      setPending(false);
    }
    if (selRef.current) { selRef.current.setEditable(false); selRef.current = null; }
    // Revert any in-flight infra edit so its overlay does not stay editable
    // while another tool takes over the map.
    const edit = editingInfraRef.current;
    if (edit && edit.obj) {
      try {
        if (edit.kind === 'pole') edit.obj.setDraggable(false);
        else edit.obj.setEditable(false);
        const orig = edit.originalGeometry;
        if (edit.kind === 'street' && orig && Array.isArray(orig.coordinates)) {
          edit.obj.setPath(orig.coordinates.map(([lng, lat]) => ({ lat, lng })));
        } else if (edit.kind === 'obstacle' && orig
          && Array.isArray(orig.coordinates) && Array.isArray(orig.coordinates[0])) {
          const ring = orig.coordinates[0].slice(0, -1);
          edit.obj.setPath(ring.map(([lng, lat]) => ({ lat, lng })));
        } else if (edit.kind === 'pole' && orig && Array.isArray(orig.coordinates)) {
          const [lng, lat] = orig.coordinates;
          edit.obj.setCenter({ lat, lng });
        }
      } catch { /* fall through; next reload will heal */ }
      setEditingInfra(null);
    }
    reportRef.current = null;
    setSelId(null);
    setReportId(null);
    setMsg(null);
    setImporting(false);
    setStreetActive(false);
    setObstacleActive(false);
    setPoleActive(false);
    setEntryActive(false);
    setGuideOpen(false);
    setKeyboardOpen(false);
    setSelectedInfra(null);
    if (next === 'building') startDraw();
    else if (next === 'kml') setImporting(true);
    else if (next === 'street') setStreetActive(true);
    else if (next === 'obstacle') setObstacleActive(true);
    else if (next === 'pole') setPoleActive(true);
    else if (next === 'entry') setEntryActive(true);
  }, [startDraw]);

  // Stable getter for the EntryPointTool's polygon hit-test - reads the
  // current polysRef without re-running the tool's effect on every render.
  const getBuildings = useCallback(
    () => polysRef.current.map((p) => ({ id: p.__buildingId, poly: p })),
    [],
  );

  // Stable getter for the ObstacleDrawTool's live overlap check. Returns the
  // raw GeoJSON Polygon geometries the operator currently sees, so the tool
  // can flag any in-flight obstacle ring that crosses a building footprint
  // without us re-creating its setup effect on every parent render.
  const getBuildingGeometries = useCallback(
    () => polysRef.current.map((p) => p.__geometry).filter(Boolean),
    [],
  );

  // Stable guard for the infrastructure click listeners: when any tool owns
  // the map cursor (building draw, Ctrl rotate, street/obstacle/pole/entry
  // tool), the click is meant for that tool, not for selecting an existing
  // street/obstacle/pole. While an infra edit is in progress we also lock
  // out new selections so the operator can't lose the half-edited state by
  // wandering off — they must save or cancel first.
  const getCanInteract = useCallback(
    () => !drawingRef.current && !ctrlRef.current && !otherToolRef.current
      && !editingInfraRef.current,
    [],
  );

  // Stable getter the InfrastructureLayer reads on every idle to decide
  // whether to pause its clearAll+refetch cycle. During edit we must keep
  // the operator's editable overlay alive on the map; once they save or
  // cancel, the very next idle refreshes the layer.
  const getInfraPaused = useCallback(() => !!editingInfraRef.current, []);

  // Delete the currently selected infra item via its kind-specific RPC, then
  // bump the reloadKey so InfrastructureLayer refreshes immediately (instead
  // of waiting for the next idle event).
  const deleteSelectedInfra = useCallback(async () => {
    if (!selectedInfra) return;
    const { kind, id } = selectedInfra;
    let result = { error: null };
    if (kind === 'street') result = await deleteStreetApi(id);
    else if (kind === 'obstacle') result = await deleteObstacleApi(id);
    else if (kind === 'pole') result = await deletePoleApi(id);
    if (result && result.error) {
      setMsgTone('error');
      setMsg('حذف ناموفق بود');
      return;
    }
    setSelectedInfra(null);
    setInfraReloadKey((k) => k + 1);
  }, [selectedInfra]);

  // Begin in-place edit of the selected infra item. We capture the current
  // geometry as the "original" before toggling Google's editable/draggable
  // affordance so cancel can put it back even after several vertex drags.
  // Polyline/Polygon use setEditable (vertex handles + midpoint inserts).
  // Circle uses setDraggable so the operator drags the whole pole to its
  // new spot — there is only one "point" to move.
  const startInfraEdit = useCallback(() => {
    if (!selectedInfra || !selectedInfra.obj || !selectedInfra.canManage) return;
    const obj = selectedInfra.obj;
    if (selectedInfra.kind === 'pole') {
      obj.setDraggable(true);
    } else {
      obj.setEditable(true);
    }
    setEditingInfra({
      kind: selectedInfra.kind,
      id: selectedInfra.id,
      obj,
      originalGeometry: selectedInfra.geometry,
    });
  }, [selectedInfra]);

  // Discard the in-flight edit and restore the overlay to its original shape
  // / position so the visual state matches what the DB still holds. Without
  // the path/center revert the operator's drags would linger on screen until
  // the next reload, which would feel like the cancel did nothing.
  const cancelInfraEdit = useCallback(() => {
    const edit = editingInfra;
    if (!edit) { setSelectedInfra(null); return; }
    const obj = edit.obj;
    const orig = edit.originalGeometry;
    try {
      if (edit.kind === 'street' && obj && orig && Array.isArray(orig.coordinates)) {
        obj.setPath(orig.coordinates.map(([lng, lat]) => ({ lat, lng })));
        obj.setEditable(false);
      } else if (edit.kind === 'obstacle' && obj && orig
        && Array.isArray(orig.coordinates) && Array.isArray(orig.coordinates[0])) {
        const ring = orig.coordinates[0].slice(0, -1); // drop the closing copy
        obj.setPath(ring.map(([lng, lat]) => ({ lat, lng })));
        obj.setEditable(false);
      } else if (edit.kind === 'pole' && obj && orig && Array.isArray(orig.coordinates)) {
        const [lng, lat] = orig.coordinates;
        obj.setCenter({ lat, lng });
        obj.setDraggable(false);
      }
    } catch { /* shape mismatch — fall through and let the next reload heal it */ }
    setEditingInfra(null);
    setSelectedInfra(null);
  }, [editingInfra]);

  // Persist the edited geometry. The shape read is canonical — we read it
  // back from the live overlay, not from React state, so any subsequent
  // user drags are included. Obstacles run the same overlap check the
  // building draw does (buildings are conceptually impassable already, so
  // an obstacle that swallows a building is meaningless and is rejected).
  const saveInfraEdit = useCallback(async () => {
    const edit = editingInfra;
    if (!edit || !edit.obj) return;
    const obj = edit.obj;
    let result = { error: null };
    setMsgTone('error');
    if (edit.kind === 'street') {
      const path = obj.getPath().getArray().map((ll) => [ll.lng(), ll.lat()]);
      if (path.length < 2) { setMsg('یک خیابان حداقل باید دو نقطه داشته باشد'); return; }
      const geometry = { type: 'LineString', coordinates: path };
      result = await updateStreetApi(edit.id, geometry);
    } else if (edit.kind === 'obstacle') {
      const path = obj.getPath().getArray().map((ll) => [ll.lng(), ll.lat()]);
      if (path.length < 3) { setMsg('یک منطقه حداقل باید سه گوشه داشته باشد'); return; }
      const ring = [...path, [path[0][0], path[0][1]]];
      const geometry = { type: 'Polygon', coordinates: [ring] };
      const buildings = polysRef.current.map((p) => p.__geometry).filter(Boolean);
      if (checkPolygon(geometry, buildings).overlapsExisting) {
        setMsg('این محدوده با یک ساختمان همپوشانی دارد');
        return;
      }
      result = await updateObstacleApi(edit.id, geometry);
    } else if (edit.kind === 'pole') {
      const c = obj.getCenter();
      const geometry = { type: 'Point', coordinates: [c.lng(), c.lat()] };
      result = await updatePoleApi(edit.id, geometry);
    }
    if (result && result.error) { setMsg('ذخیره ناموفق بود'); return; }
    // Disable the edit affordance before reload swaps the overlay for a
    // fresh one — otherwise Google's editable handles would briefly show
    // on a stale object.
    if (edit.kind === 'pole') obj.setDraggable(false);
    else obj.setEditable(false);
    setEditingInfra(null);
    setSelectedInfra(null);
    setMsg(null);
    setInfraReloadKey((k) => k + 1);
  }, [editingInfra]);

  // Any tool that owns the map cursor / hosts a top-center HUD; suppresses
  // the top toolbar + search while it is active so the panels do not collide.
  const toolHudActive = drawing || streetActive || obstacleActive
    || poleActive || entryActive;

  return (
    <>
      {!toolHudActive && (
        <TopControlBar
          onActivate={activateTool}
          onPick={goTo}
        />
      )}
      {drawing && (
        <DrawHUD
          count={vertCount}
          overlap={drawOverlap}
          onFinish={finishDraw}
          onUndo={undoVertex}
          onCancel={cancelDrawMode}
        />
      )}
      <MapTypeToggle value={mapType} onChange={setMapType} />
      <MapRotateControl map={map} />
      <BottomLeftHelpCluster
        onGuide={() => setGuideOpen(true)}
        onKeyboard={() => setKeyboardOpen(true)}
      />
      {pending && <PendingPanel onSave={saveDraw} onCancel={cancelDraw} />}
      {selId && (
        <SelectionPanel
          mode={selMode}
          ownerName={selMeta.ownerName}
          reportCount={selMeta.reportCount}
          onSave={saveEdit}
          onDelete={removeSel}
          buildingId={selId}
        />
      )}
      {reportId && (
        <ReportPanel ownerName={reportOwner} onSubmit={submitReport} onCancel={cancelReport} />
      )}
      {hoverInfo && !drawing && <HoverPill info={hoverInfo} />}
      {importing && (
        <KmlImportPanel
          onClose={() => setImporting(false)}
          onCompleted={reload}
        />
      )}
      <StreetDrawTool
        map={map}
        active={streetActive}
        snapPoints={snapPoints}
        onClose={() => setStreetActive(false)}
        onSaved={() => setInfraReloadKey((k) => k + 1)}
      />
      <ObstacleDrawTool
        map={map}
        active={obstacleActive}
        getBuildingGeometries={getBuildingGeometries}
        onClose={() => setObstacleActive(false)}
        onSaved={() => setInfraReloadKey((k) => k + 1)}
      />
      <PoleDropTool
        map={map}
        active={poleActive}
        onClose={() => setPoleActive(false)}
        onSaved={() => setInfraReloadKey((k) => k + 1)}
      />
      <EntryPointTool
        map={map}
        active={entryActive}
        getBuildings={getBuildings}
        onClose={() => setEntryActive(false)}
        onSaved={() => reload()}
      />
      <InfrastructureLayer
        map={map}
        reloadKey={infraReloadKey}
        onSnapPoints={setSnapPoints}
        onSelectInfra={setSelectedInfra}
        getCanInteract={getCanInteract}
        isPaused={getInfraPaused}
      />
      {selectedInfra && (
        <InfraSelectionPanel
          item={selectedInfra}
          editing={!!editingInfra && editingInfra.id === selectedInfra.id}
          onEdit={startInfraEdit}
          onSave={saveInfraEdit}
          onCancel={cancelInfraEdit}
          onDelete={deleteSelectedInfra}
          onClose={() => setSelectedInfra(null)}
        />
      )}
      {guideOpen && <OperatorGuideModal onClose={() => setGuideOpen(false)} />}
      {keyboardOpen && <KeyboardShortcutsModal onClose={() => setKeyboardOpen(false)} />}
      {msg && <MapAlert message={msg} tone={msgTone} />}
      {error && !msg && <MapAlert message={error} />}
      {zoomGated && !msg && !error && (
        <MapAlert
          message={overviewMode
            ? 'نمای کلی ساختمان‌ها - برای ویرایش، بیشتر زوم کنید'
            : 'برای نمایش ساختمان‌ها بیشتر زوم کنید'}
          tone="success"
        />
      )}
    </>
  );
}

export default function AnnotationMap({ operatorFilter = null, initialBounds = null, focusBuildingId = null }) {
  // Lifted so the Layer's density gate (computed on every map idle) can toggle the
  // sibling deck.gl MVT layer: visible only when the viewport is too dense for the
  // editable Polygon layer. Declared before the early return to satisfy
  // rules-of-hooks.
  const [dense, setDense] = useState(false);
  if (!KEY) {
    return (
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f1f5f9',
          fontFamily: "'Vazirmatn', sans-serif",
          direction: 'rtl',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            maxWidth: 420,
            padding: '28px 32px',
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            borderRadius: 14,
            boxShadow:
              '0 1px 0 rgba(255,255,255,0.6) inset, 0 10px 30px rgba(30,58,138,0.1)',
            textAlign: 'center',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              border: '1px solid #fecaca',
              borderRadius: 12,
              fontSize: 22,
              fontWeight: 700,
              color: '#dc2626',
              lineHeight: 1,
            }}
          >
            !
          </span>
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.08em',
                color: '#94a3b8',
                marginBottom: 6,
                fontFamily: "'Vazirmatn', monospace",
              }}
            >
              CONFIG ERROR
            </div>
            <div style={{ fontSize: 15, fontWeight: 600, color: '#1e3a8a', lineHeight: 1.6 }}>
              کلید Google Maps تنظیم نشده است
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <APIProvider apiKey={KEY} language="fa" region="IR">
      <Map mapId="fttx-operator" defaultCenter={{ lat: 35.7, lng: 51.4 }} defaultZoom={15}
        gestureHandling="greedy" disableDefaultUI={false}
        renderingType="VECTOR" defaultTilt={0}
        style={{ width: '100%', height: '100%' }} />
      <Layer operatorFilter={operatorFilter} initialBounds={initialBounds} focusBuildingId={focusBuildingId}
        onDensityChange={MVT_BUILDINGS_ENABLED ? setDense : null} />
      {MVT_BUILDINGS_ENABLED && <BuildingsRasterLayer visible={dense} />}
    </APIProvider>
  );
}
